import React, { useEffect, useState } from 'react';
import { organizationService } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Building2, Users, Check, Clock, LogOut, KeyRound, UserPlus, Shield, Wallet, Plus, Search, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const emptyForm = { name: '', description: '', join_policy: 'both', image_url: '', settlement_enabled: false };

export default function Organizations() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = !!user?.is_admin;

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [codes, setCodes] = useState({});
  const [paypals, setPaypals] = useState({});
  const [needPaypal, setNeedPaypal] = useState({});
  const [busy, setBusy] = useState(null);
  const [requests, setRequests] = useState({});
  const [openReq, setOpenReq] = useState(null);
  const [leaveTarget, setLeaveTarget] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editOrg, setEditOrg] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const [membersOrg, setMembersOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null); // { org, member }

  const inPaypalOrg = orgs.some(o => o.membership === 'member' && o.settlement_enabled);

  const load = async () => {
    try {
      const res = await organizationService.getAll();
      setOrgs(res.data || []);
    } catch (e) {
      toast.error(t('orgLoadFailed'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doJoin = async (org, finalize = false) => {
    const code = (codes[org.id] || '').trim();
    const paypal = (paypals[org.id] || '').trim();
    if (!finalize && !code) { toast.error(t('orgEnterCode')); return; }
    setBusy(org.id);
    try {
      await organizationService.join(org.id, code, paypal || undefined);
      toast.success(t('orgJoined'));
      setNeedPaypal(p => ({ ...p, [org.id]: false }));
      await load();
    } catch (e) {
      if (e.response?.data?.needs_paypal) {
        setNeedPaypal(p => ({ ...p, [org.id]: true }));
        toast.error(e.response.data.detail || t('orgPaypalRequired'));
      } else {
        toast.error(e.response?.data?.detail || t('orgJoinFailed'));
      }
    } finally { setBusy(null); }
  };

  const doRequest = async (org) => {
    setBusy(org.id);
    try {
      await organizationService.request(org.id);
      toast.success(t('orgRequestSent'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('orgRequestFailed'));
    } finally { setBusy(null); }
  };

  const confirmLeave = async () => {
    const org = leaveTarget; setLeaveTarget(null);
    if (!org) return;
    setBusy(org.id);
    try {
      await organizationService.leave(org.id);
      toast.success(t('orgLeft'));
      await load();
    } catch (e) { toast.error(t('orgLeaveFailed')); } finally { setBusy(null); }
  };

  const toggleRequests = async (org) => {
    if (openReq === org.id) { setOpenReq(null); return; }
    setOpenReq(org.id);
    try {
      const res = await organizationService.getRequests(org.id);
      setRequests(prev => ({ ...prev, [org.id]: res.data || [] }));
    } catch (e) { toast.error(t('orgRequestsLoadFailed')); }
  };

  const decide = async (org, rid, approve) => {
    try {
      if (approve) await organizationService.approve(org.id, rid);
      else await organizationService.reject(org.id, rid);
      const res = await organizationService.getRequests(org.id);
      setRequests(prev => ({ ...prev, [org.id]: res.data || [] }));
      toast.success(approve ? t('orgApprovedToast') : t('orgRejectedToast'));
      load();
    } catch (e) { toast.error(t('orgActionFailed')); }
  };

  const doCreate = async () => {
    if (!createForm.name.trim()) { toast.error(t('orgNameRequired')); return; }
    setBusy('create');
    try {
      await organizationService.create(createForm);
      toast.success(t('orgCreated'));
      setShowCreate(false); setCreateForm(emptyForm);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t('orgCreateFailed')); }
    finally { setBusy(null); }
  };

  const openEdit = (org) => {
    setEditOrg(org);
    setEditForm({
      name: org.name || '', description: org.description || '', image_url: org.image_url || '',
      join_policy: org.join_policy || 'both', settlement_enabled: !!org.settlement_enabled,
    });
  };
  const doUpdate = async () => {
    if (!editOrg) return;
    setBusy('edit');
    try {
      const payload = {
        name: editForm.name, description: editForm.description,
        image_url: editForm.image_url, join_policy: editForm.join_policy,
      };
      if (isAdmin) payload.settlement_enabled = editForm.settlement_enabled;
      await organizationService.update(editOrg.id, payload);
      toast.success(t('orgUpdated'));
      setEditOrg(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t('orgSaveFailed')); }
    finally { setBusy(null); }
  };

  const openMembers = async (org) => {
    setMembersOrg(org); setMembers([]);
    try {
      const res = await organizationService.getMembers(org.id);
      setMembers(res.data || []);
    } catch (e) { toast.error(t('orgMembersLoadFailed')); }
  };
  const confirmRemove = async () => {
    const { org, member } = removeTarget || {};
    setRemoveTarget(null);
    if (!org || !member) return;
    try {
      await organizationService.removeMember(org.id, member.id);
      toast.success(t('orgMemberRemoved'));
      const res = await organizationService.getMembers(org.id);
      setMembers(res.data || []);
      load();
    } catch (e) { toast.error(t('orgRemoveFailed')); }
  };

  const filtered = orgs.filter(o => {
    const q = search.trim().toLowerCase();
    return !q || o.name.toLowerCase().includes(q) || (o.description || '').toLowerCase().includes(q);
  });

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground animate-pulse">{t('loading')}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-primary/10 text-primary"><Building2 className="w-5 h-5" /></div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t('orgsTitle')}</h1>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-1.5"><Plus className="w-4 h-4" /> {t('orgsCreateBtn')}</Button>
      </div>
      <p className="text-muted-foreground text-sm mb-4">
        {t('orgsSubtitle')}
      </p>

      <div className="relative mb-6">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('orgsSearch')} className="pl-9" />
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">{t('orgsNoneFound')}</p>
        )}
        {filtered.map(org => {
          const isMember = org.membership === 'member';
          const isPending = org.membership === 'pending';
          const isAccepted = org.membership === 'accepted';
          const isHost = org.role === 'host';
          const canCode = ['code', 'both'].includes(org.join_policy);
          const canRequest = ['request', 'both'].includes(org.join_policy);
          const reqList = requests[org.id] || [];

          return (
            <Card key={org.id} className="p-5 border-border/40 bg-gradient-to-b from-card/60 to-card/20 backdrop-blur-md rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:border-border/60 transition-colors">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  {org.image_url && <img src={org.image_url} alt="" className="w-12 h-12 rounded-xl object-cover border border-border/40 shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-semibold">{org.name}</h2>
                      {inPaypalOrg && (org.settlement_enabled ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
                          <Wallet className="w-3 h-3" /> {t('orgPaypalBilling')}
                        </span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground border border-border/30">{t('orgPlayMoney')}</span>
                      ))}
                      {isHost && (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          <Shield className="w-3 h-3" /> {t('orgHostBadge')}
                        </span>
                      )}
                    </div>
                    {org.description && <p className="text-sm text-muted-foreground mt-1">{org.description}</p>}
                    {isMember ? (
                      <button onClick={() => openMembers(org)} className="text-xs text-muted-foreground mt-2 flex items-center gap-1 hover:text-foreground transition-colors">
                        <Users className="w-3.5 h-3.5" /> {org.member_count} {org.member_count === 1 ? t('orgMemberWord') : t('orgMembersWord')} · {t('orgViewMembers')}
                      </button>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> {org.member_count} {org.member_count === 1 ? t('orgMemberWord') : t('orgMembersWord')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  {isMember && <span className="inline-flex items-center gap-1 text-sm text-green-500 font-medium"><Check className="w-4 h-4" /> {t('orgMemberBadge')}</span>}
                  {isPending && <span className="inline-flex items-center gap-1 text-sm text-amber-500 font-medium"><Clock className="w-4 h-4" /> {t('orgRequestedBadge')}</span>}
                  {isAccepted && <span className="inline-flex items-center gap-1 text-sm text-primary font-medium"><Check className="w-4 h-4" /> {t('orgAcceptedBadge')}</span>}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {!isMember && !isPending && !isAccepted && canCode && (
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <KeyRound className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={codes[org.id] || ''} onChange={e => setCodes(p => ({ ...p, [org.id]: e.target.value }))} placeholder={t('orgJoinCode')} className="pl-9 w-40 h-10" />
                      </div>
                      <Button disabled={busy === org.id} onClick={() => doJoin(org)}>{t('orgJoinBtn')}</Button>
                    </div>
                  )}
                  {!isMember && !isPending && !isAccepted && canRequest && (
                    <Button variant="outline" disabled={busy === org.id} onClick={() => doRequest(org)} className="gap-1.5"><UserPlus className="w-4 h-4" /> {t('orgRequestJoin')}</Button>
                  )}
                  {isMember && (
                    <>
                      {isHost && (
                        <Button variant="outline" onClick={() => openEdit(org)} className="gap-1.5"><Pencil className="w-4 h-4" /> {t('orgEditBtn')}</Button>
                      )}
                      {isHost && (
                        <Button variant="outline" onClick={() => toggleRequests(org)} className="gap-1.5 border-primary/30 text-primary hover:bg-primary/10"><Shield className="w-4 h-4" /> {t('orgRequestsBtn')}</Button>
                      )}
                      <Button variant="ghost" disabled={busy === org.id} onClick={() => setLeaveTarget(org)} className="gap-1.5 text-muted-foreground hover:text-red-500"><LogOut className="w-4 h-4" /> {t('orgLeaveBtn')}</Button>
                    </>
                  )}
                </div>

                {!isMember && !isPending && !isAccepted && needPaypal[org.id] && (
                  <div className="flex items-center gap-2 flex-wrap rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                    <div className="relative">
                      <Wallet className="w-4 h-4 text-amber-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input value={paypals[org.id] || ''} onChange={e => setPaypals(p => ({ ...p, [org.id]: e.target.value }))} placeholder="https://paypal.me/deinname" className="pl-9 w-64 h-10" />
                    </div>
                    <Button disabled={busy === org.id} onClick={() => doJoin(org)}>{t('orgJoinWithPaypal')}</Button>
                  </div>
                )}

                {isAccepted && (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-3">
                    <p className="text-sm">{t('orgAcceptedInfo')}{' '}{org.settlement_enabled ? t('orgFinishPaypal') : t('orgFinishJoin')}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {org.settlement_enabled && (
                        <div className="relative">
                          <Wallet className="w-4 h-4 text-amber-500 absolute left-3 top-1/2 -translate-y-1/2" />
                          <Input value={paypals[org.id] || ''} onChange={e => setPaypals(p => ({ ...p, [org.id]: e.target.value }))} placeholder="https://paypal.me/deinname" className="pl-9 w-64 h-10" />
                        </div>
                      )}
                      <Button disabled={busy === org.id} onClick={() => doJoin(org, true)}>{t('orgCompleteJoin')}</Button>
                    </div>
                  </div>
                )}
              </div>

              {isHost && openReq === org.id && (
                <div className="mt-4 border-t border-border/30 pt-4">
                  {reqList.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('orgNoRequests')}</p>
                  ) : (
                    <div className="space-y-2">
                      {reqList.map(r => (
                        <div key={r.id} className="flex items-center justify-between gap-3 text-sm">
                          <div className="min-w-0"><span className="font-medium">{r.username}</span><span className="text-muted-foreground"> · {r.email}</span></div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" onClick={() => decide(org, r.id, true)} className="bg-green-600 hover:bg-green-600/90">{t('approveBtn')}</Button>
                            <Button size="sm" variant="outline" onClick={() => decide(org, r.id, false)} className="text-red-500 hover:text-red-500 hover:bg-red-500/10 border-red-500/30">{t('rejectBtn')}</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Verlassen */}
      <AlertDialog open={!!leaveTarget} onOpenChange={(o) => !o && setLeaveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('orgLeaveTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{leaveTarget?.name} — {t('orgLeaveDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeave} className="bg-red-600 hover:bg-red-600/90">{t('orgLeaveBtn')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mitglied entfernen */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('orgRemoveMemberTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{removeTarget?.member?.username} · {removeTarget?.org?.name} — {t('orgRemoveMemberDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-600 hover:bg-red-600/90">{t('removeBtn')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Erstellen */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orgCreateTitle')}</DialogTitle>
            <DialogDescription>{t('orgCreateDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>{t('orgNameLabel')}</Label>
              <Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="z. B. Mathe LK 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('orgDescLabel')}</Label>
              <Textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder={t('orgDescPlaceholder')} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('orgImageOptional')}</Label>
              <Input value={createForm.image_url} onChange={e => setCreateForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://…" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('orgPolicyLabel')}</Label>
              <Select value={createForm.join_policy} onValueChange={v => setCreateForm(f => ({ ...f, join_policy: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">{t('policyBoth')}</SelectItem>
                  <SelectItem value="code">{t('policyCode')}</SelectItem>
                  <SelectItem value="request">{t('policyRequest')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-500" /><span className="text-sm">{t('orgPaypalBilling')}</span></div>
                <Switch checked={createForm.settlement_enabled} onCheckedChange={v => setCreateForm(f => ({ ...f, settlement_enabled: v }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t('cancel')}</Button>
            <Button disabled={busy === 'create'} onClick={doCreate}>{t('orgsCreateBtn')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bearbeiten */}
      <Dialog open={!!editOrg} onOpenChange={(o) => !o && setEditOrg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orgEditTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>{t('orgNameLabel')}</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('orgDescLabel')}</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('orgImageLabel')}</Label>
              <Input value={editForm.image_url} onChange={e => setEditForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://…" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('orgPolicyLabel')}</Label>
              <Select value={editForm.join_policy} onValueChange={v => setEditForm(f => ({ ...f, join_policy: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">{t('policyBoth')}</SelectItem>
                  <SelectItem value="code">{t('policyCode')}</SelectItem>
                  <SelectItem value="request">{t('policyRequest')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-500" /><span className="text-sm">{t('orgPaypalBilling')}</span></div>
                <Switch checked={editForm.settlement_enabled} onCheckedChange={v => setEditForm(f => ({ ...f, settlement_enabled: v }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrg(null)}>{t('cancel')}</Button>
            <Button disabled={busy === 'edit'} onClick={doUpdate}>{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mitglieder */}
      <Dialog open={!!membersOrg} onOpenChange={(o) => !o && setMembersOrg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orgMembersTitle')} · {membersOrg?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 py-2 max-h-[50vh] overflow-y-auto">
            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{t('loading')}</p>
            ) : members.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-3 py-2 px-1">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={m.avatar_url} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">{m.username?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium truncate">{m.username}</span>
                  {m.role === 'host' && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary"><Shield className="w-2.5 h-2.5" /> {t('orgHostBadge')}</span>}
                </div>
                {membersOrg?.role === 'host' && m.id !== user?.id && (
                  <Button size="sm" variant="ghost" onClick={() => setRemoveTarget({ org: membersOrg, member: m })} className="text-muted-foreground hover:text-red-500 h-8 w-8 p-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
